import React, { useEffect, useState } from 'react';
import { AutoComplete, Spin } from 'antd';
import FiltersService from '../../services/FiltersService';

interface StateAutoCompleteProps {
  value: string;
  onChange: (value: string, option?: { label: string, value: string }) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onBlur?: () => void;
  onSelect?: (value: string, option: { label: string, value: string }) => void;
  size?: 'small' | 'middle' | 'large';
}

const StateAutoComplete: React.FC<StateAutoCompleteProps> = ({
  value,
  onChange,
  error,
  placeholder = 'State *',
  disabled = false,
  className = '',
  onBlur,
  onSelect,
  size = 'large'
}) => {
  const [options, setOptions] = useState<{ label: string, value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    FiltersService.getStates()
      .then(states => {
        setOptions(states);
        // Find the state name for the current value (if it's a state code)
        if (value) {
          const selectedState = states.find(state => state.value === value);
          if (selectedState) {
            setDisplayValue(selectedState.label);
          } else {
            setDisplayValue(value);
          }
        } else {
          setDisplayValue('');
        }
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [value]);

  return (
    <div
      className="custom-autocomplete-wrapper"
      tabIndex={-1}
    >
      <AutoComplete
        className={className + (error ? ' is-invalid' : '')}
        placeholder={placeholder}
        value={displayValue}
        options={options}
        disabled={disabled}
        size={size}
        filterOption={(inputValue, option) =>
          option && option.label && typeof option.label === 'string'
            ? option.label.toLowerCase().includes(inputValue.toLowerCase())
            : false
        }
        onSelect={(value, option) => {
          const selectedOption = option as { label: string, value: string };
          setDisplayValue(selectedOption.label);
          if (onSelect) onSelect(selectedOption.value, selectedOption);
          onChange(selectedOption.value, selectedOption);
        }}
        onChange={(inputValue) => {
          setDisplayValue(inputValue);
          // If user is typing, clear the value to indicate no selection
          onChange('', undefined);
        }}
        notFoundContent={loading ? <Spin size="small" /> : null}
        allowClear
        onBlur={onBlur}
      />
    </div>
  );
};

export default StateAutoComplete; 